import { modelById } from './constants.js';
import { hasPrice, quotedCost } from './costs.js';

export function recordingFor(S, cam) {
  const model = modelById(S, cam.modelId);
  return { codec: 'H.265', fps: 15, hours: 24, activityPct: 100, retentionDays: 30, liveHours: 8,
    ...model?.details?.recording, ...S.install?.recording, ...cam.recording };
}

export function recordingEstimate(S) {
  const issues = [];
  const cameras = S.cams.map(cam => {
    const r = recordingFor(S, cam), m = modelById(S, cam.modelId);
    const bitrate = r.bitrateMbps > 0 ? r.bitrateMbps : null;
    const live = r.liveMbps > 0 ? r.liveMbps : 0;
    const seconds = r.hours * 3600 * r.retentionDays * r.activityPct / 100;
    const storageTB = bitrate == null ? null : bitrate * 1e6 * seconds / 8 / 1e12;
    if (bitrate == null) issues.push(`${cam.name}: enter a measured or estimated recording bitrate.`);
    const channels = Math.max(1, m?.details?.sensorModules?.length || 1);
    return { cam, r, bitrate, live, storageTB, peakMbps: (bitrate || 0) + live, channels };
  });
  const recorders = (S.equipment || []).filter(e => e.kind === 'recorder').map(device => {
    const rows = cameras.filter(c => c.cam.recorderId === device.id);
    const channels = rows.reduce((n, c) => n + c.channels, 0), storageTB = rows.reduce((n, c) => n + (c.storageTB || 0), 0);
    const bitrate = rows.reduce((n, c) => n + (c.bitrate || 0), 0), unknown = rows.filter(c => c.bitrate == null).length;
    if (channels > (device.channels || 0)) issues.push(`${device.name}: ${channels} streams exceed ${device.channels || 0} recording channels.`);
    if (channels > (device.licenseChannels || 0)) issues.push(`${device.name}: ${channels} streams exceed ${device.licenseChannels || 0} licensed channels.`);
    if (storageTB > (device.storageTB || 0)) issues.push(`${device.name}: ${storageTB.toFixed(2)} TB exceeds the entered usable storage.`);
    if (bitrate > (device.bandwidthMbps || 0)) issues.push(`${device.name}: ${bitrate.toFixed(1)} Mbps exceeds recorder throughput.`);
    return { device, cameras: rows, channels, storageTB, bitrate, unknown };
  });
  for (const row of cameras) if (!recorders.some(r => r.device.id === row.cam.recorderId)) issues.push(`${row.cam.name}: no recorder assigned.`);
  return { cameras, recorders, issues, storageTB: cameras.reduce((n, c) => n + (c.storageTB || 0), 0),
    peakMbps: cameras.reduce((n, c) => n + c.peakMbps, 0), unknown: cameras.filter(c => c.bitrate == null).length };
}

export function cameraAllowance(S, cam) {
  const i = S.install || {};
  return ['labourPerCam', 'mountPerCam', 'licensePerCam'].reduce((n, key) => n + quotedCost(i[key], i.quoteStatus?.[key]), 0);
}
export function planningCameraCost(S, cam, cfg) {
  const model = modelById(S, cam.modelId);
  const price = hasPrice(model, S.opt.currency) ? model.cost : 0;
  if (cfg.costBasis !== 'installed') return price;
  const routes = (S.cables || []).filter(r => r.camId === cam.id && S.equipment.some(e => e.id === r.equipmentId));
  let length = routes.length ? 0 : (S.install?.cableAllowance || 0);
  for (const r of routes) {
    const e = S.equipment.find(e => e.id === r.equipmentId), pts = [cam, ...r.pts, e];
    length += Math.abs(cam.h - r.height) + Math.abs(e.h - r.height) + r.extra;
    for (let i = 1; i < pts.length; i++) length += Math.hypot(pts[i].x - pts[i - 1].x, pts[i].y - pts[i - 1].y);
  }
  return price + cameraAllowance(S, cam) + length * quotedCost(S.install?.cableCost, S.install?.quoteStatus?.cableCost) * (1 + (S.install?.slackPct || 0) / 100);
}
